// @ts-nocheck
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import GiteaStatusDot from "../components/gitea/GiteaStatusDot";
import Badge from "../components/Badge";
import Spinner from "../components/Spinner";
import ErrorPage from "../components/ErrorPage";
import { LdsOverviewHero, LdsOverviewPage } from "../components/lds/LdsOverview";
import { LdsMarkdown } from "../components/lds/LdsMarkdown";
import {
  mergeIssueData,
  resolveIssueKind,
  resolveIssueTypeDisplay,
  resolveGiteaDrawerClass,
} from "./giteaIssueModel";
import {
  LdsTable,
  LdsTableBody,
  LdsTableCard,
  LdsTableCell,
  LdsTableHead,
  LdsTableHeadCell,
  LdsTableRow,
  LdsTableScroll,
} from "../components/lds/LdsTable";
import http from "../api/apiClient";
import config from "../config";
import { mapHttpErrorToMessage } from "../utils/mapHttpErrorToMessage";
import { useCan } from "../authz/useCan";
import { useSupportViewAs } from "../supportViewAs/SupportViewAsProvider";
import { isClosedWorkflowStatus } from "./giteaWorkflowStatus";
import {
  FaCheck,
  FaEnvelope,
  FaExclamationCircle,
  FaGitAlt,
  FaLock,
  FaLockOpen,
  FaPaperPlane,
  FaPaperclip,
  FaPen,
  FaTrash,
  FaUserSecret,
} from "react-icons/fa";

import { GiteaCreateIssue } from "./GiteaCreateIssue";
import { lumenSplitLinks } from "./giteaSplitLinks";
import { GiteaIssueOwnerSelect } from "./GiteaIssueOwnerSelect";
const parseDateTime = (value) => {
  if (!value) return null;
  const raw = String(value).trim();
  if (!raw) return null;

  // Support dd-mm-yyyy (optionally with time)
  const dmyMatch = raw.match(
    /^(\d{2})-(\d{2})-(\d{4})(?:[,\sT]+(\d{2}):(\d{2})(?::(\d{2}))?)?/
  );
  if (dmyMatch) {
    const [, dd, mm, yyyy, hh = "00", min = "00", ss = "00"] = dmyMatch;
    const normalized = `${yyyy}-${mm}-${dd}T${hh}:${min}:${ss}`;
    const d = new Date(normalized);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  // Numeric timestamp (seconds or milliseconds), optionally with decimals.
  if (/^\d+(?:\.\d+)?$/.test(raw)) {
    const numeric = Number(raw);
    if (!Number.isFinite(numeric)) return null;
    const ms = numeric > 1e12 ? numeric : numeric * 1000;
    const d = new Date(ms);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  let normalized = raw;
  if (normalized.includes(" ") && !normalized.includes("T")) {
    normalized = normalized.replace(" ", "T");
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(normalized)) {
    normalized = `${normalized}T00:00:00`;
  }
  const d = new Date(normalized);
  return Number.isNaN(d.getTime()) ? null : d;
};

const formatDateTime = (value) => {
  const d = parseDateTime(value);
  if (!d) return value ? String(value) : "-";

  const raw = value == null ? "" : String(value).trim();
  const hasTimezone = /Z$|[+-]\d{2}:\d{2}$/.test(raw);
  const useUTC = hasTimezone;

  const pad2 = (n) => String(n).padStart(2, "0");
  const yyyy = useUTC ? d.getUTCFullYear() : d.getFullYear();
  const mm = pad2((useUTC ? d.getUTCMonth() : d.getMonth()) + 1);
  const dd = pad2(useUTC ? d.getUTCDate() : d.getDate());
  const hh = pad2(useUTC ? d.getUTCHours() : d.getHours());
  const min = pad2(useUTC ? d.getUTCMinutes() : d.getMinutes());

  return `${dd}-${mm}-${yyyy}, ${hh}:${min}`;
};

const isOlderThanDays = (value, days) => {
  const d = parseDateTime(value);
  if (!d) return false;
  const ageMs = Date.now() - d.getTime();
  return ageMs > days * 24 * 60 * 60 * 1000;
};

const stateChipClass = (state) => {
  const normalized = String(state || "").toLowerCase();
  if (normalized === "open") return "bg-emerald-500/15 text-emerald-700 dark:text-emerald-200";
  if (normalized === "closed") return "bg-slate-500/15 text-slate-700 dark:text-slate-200";
  return "bg-indigo-500/15 text-indigo-700 dark:text-indigo-200";
};

const workflowChipClass = (status) => {
  const normalized = String(status || "").trim().toLowerCase();
  if (!normalized) return "bg-slate-500/10 text-slate-700 dark:text-slate-200";
  if (["new", "nieuw"].includes(normalized)) return "bg-sky-500/15 text-sky-700 dark:text-sky-200";
  if (["in progress", "in behandeling"].includes(normalized))
    return "bg-amber-500/15 text-amber-800 dark:text-amber-200";
  if (["on hold", "in wacht"].includes(normalized))
    return "bg-rose-500/15 text-rose-700 dark:text-rose-200";
  if (["rejected", "afgewezen"].includes(normalized))
    return "bg-rose-500/15 text-rose-700 dark:text-rose-200";
  if (["closed", "gesloten"].includes(normalized))
    return "bg-slate-500/15 text-slate-700 dark:text-slate-200";
  if (["done", "resolved", "opgelost"].includes(normalized))
    return "bg-emerald-500/15 text-emerald-700 dark:text-emerald-200";
  if (["blocked", "geblokkeerd"].includes(normalized))
    return "bg-rose-500/15 text-rose-700 dark:text-rose-200";
  return "bg-indigo-500/15 text-indigo-700 dark:text-indigo-200";
};

const workflowStatusLabel = (value, t) => {
  const raw = value == null ? "" : String(value).trim();
  const normalized = raw.toLowerCase();
  if (!raw) return "";
  if (normalized === "new" || normalized === "nieuw")
    return t("gitea.workflow_status.new", { defaultValue: "Nieuw" });
  if (normalized === "in progress" || normalized === "in behandeling")
    return t("gitea.workflow_status.in_progress", { defaultValue: "In behandeling" });
  if (normalized === "on hold" || normalized === "in wacht")
    return t("gitea.workflow_status.on_hold", { defaultValue: "In wacht" });
  if (normalized === "closed" || normalized === "gesloten")
    return t("gitea.workflow_status.closed", { defaultValue: "Gesloten" });
  if (normalized === "rejected" || normalized === "afgewezen")
    return t("gitea.workflow_status.rejected", { defaultValue: "Afgewezen" });
  return raw;
};

const WORKFLOW_STATUS_PRESETS = [
  { value: "New", labelKey: "gitea.workflow_status.new", defaultValue: "New" },
  { value: "In Progress", labelKey: "gitea.workflow_status.in_progress", defaultValue: "In progress" },
  { value: "On Hold", labelKey: "gitea.workflow_status.on_hold", defaultValue: "On hold" },
  { value: "Closed", labelKey: "gitea.workflow_status.closed", defaultValue: "Closed" },
  { value: "Rejected", labelKey: "gitea.workflow_status.rejected", defaultValue: "Rejected" },
];

const formatReporter = (value) => {
  if (!value) return "-";
  if (typeof value === "string") return value;
  if (typeof value === "object") {
    return (
      value?.username ||
      value?.login ||
      value?.name ||
      value?.displayName ||
      value?.fullName ||
      value?.email ||
      "-"
    );
  }
  return String(value);
};

const formatReporterEmail = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value.includes("@") ? value : "";
  if (typeof value === "object") {
    const email = value?.email || value?.mail || "";
    return typeof email === "string" && email.includes("@") ? email : "";
  }
  return "";
};

const extractApiErrorMessage = (error) => {
  const data = error?.response?.data;
  if (typeof data === "string" && data.trim()) return data.trim();
  if (data && typeof data === "object") {
    const message = data?.message ?? data?.error ?? data?.title ?? null;
    if (typeof message === "string" && message.trim()) return message.trim();
    try {
      return JSON.stringify(data);
    } catch {
      // ignore
    }
  }
  const message = error?.message ?? null;
  return typeof message === "string" && message.trim() ? message.trim() : "";
};

const parseIssueBody = (raw) => {
  const text = raw == null ? "" : String(raw);
  if (!text.trim()) return { summary: "", meta: [], details: "" };

  const lines = text.replace(/\r\n/g, "\n").split("\n");
  const delimiterIndex = lines.findIndex((l) => l.trim() === "---");
  if (delimiterIndex === -1) {
    return { summary: text.trim(), meta: [], details: "" };
  }

  const summary = lines.slice(0, delimiterIndex).join("\n").trim();
  const metaLines = lines.slice(delimiterIndex + 1);

  const meta = [];
  const rest = [];
  for (const line of metaLines) {
    const m = line.match(/^\*\*(.+?)\*\*:\s*(.*)\s*$/);
    if (m) {
      meta.push({ key: m[1].trim(), value: m[2].trim() });
    } else {
      rest.push(line);
    }
  }

  return { summary, meta, details: rest.join("\n").trim() };
};

const extractImageUrls = (raw) => {
  const text = raw == null ? "" : String(raw);
  if (!text.trim()) return [];

  const urls = [];
  const seen = new Set();
  const push = (value) => {
    const v = String(value || "").trim();
    if (!v) return;
    if (seen.has(v)) return;
    seen.add(v);
    urls.push(v);
  };

  try {
    const mdImg = /!\[[^\]]*\]\(([^)\s]+)(?:\s+\"[^\"]*\")?\)/g;
    let m;
    while ((m = mdImg.exec(text))) push(m[1]);
  } catch {
    // ignore
  }

  try {
    const dataImg = /(data:image\/[a-zA-Z0-9.+-]+;base64,[A-Za-z0-9+/=]+)\b/g;
    let m;
    while ((m = dataImg.exec(text))) push(m[1]);
  } catch {
    // ignore
  }

  try {
    const httpImg = /(https?:\/\/[^\s)]+?\.(?:png|jpe?g|gif|webp))(?:\?[^\s)]*)?/gi;
    let m;
    while ((m = httpImg.exec(text))) push(m[0]);
  } catch {
    // ignore
  }

  return urls;
};

const normalizeAssets = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.assets)) return data.assets;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.content)) return data.content;
  return [];
};

const assetDisplay = (asset) => {
  if (!asset || typeof asset !== "object") return { id: "", name: "", url: "", contentType: "" };
  const id = asset.id ?? asset.assetId ?? asset.attachmentId ?? asset.fileId ?? "";
  const name = asset.name || asset.fileName || asset.filename || asset.path || "";
  const contentType = asset.contentType || asset.mimeType || asset.type || "";
  const url = asset.downloadUrl || asset.browserDownloadUrl || asset.url || asset.href || "";
  return {
    id: String(id ?? ""),
    name: String(name || ""),
    url: String(url || ""),
    contentType: String(contentType || ""),
  };
};

const isImageAsset = ({ name, contentType, url }) => {
  const ct = String(contentType || "").toLowerCase();
  if (ct.startsWith("image/")) return true;
  const target = String(name || url || "").toLowerCase();
  return /\.(png|jpe?g|gif|webp|bmp|svg)$/.test(target);
};

const buildProxyAssetUrl = (baseURL, issueNumber, assetId) => {
  const b = baseURL == null ? "" : String(baseURL).replace(/\/+$/, "");
  const n = issueNumber == null ? "" : String(issueNumber).trim();
  const id = assetId == null ? "" : String(assetId).trim();
  if (!b || !n || !id) return "";
  return `${b}/issues/${encodeURIComponent(n)}/assets/${encodeURIComponent(id)}`;
};

const isProtectedAssetUrl = (url, baseURL) => {
  const u = String(url || "").trim();
  if (!u) return false;
  const b = String(baseURL || "").replace(/\/+$/, "");
  if (b && u.startsWith(b)) return true;
  if (u.includes("/api/v1/issues/") && u.includes("/assets/")) return true;
  // Treat any issue-assets style URL as protected (proxy endpoint requires Bearer auth).
  if (u.includes("/issues/") && u.includes("/assets/")) return true;
  return /\/issues\/[^/]+\/assets\/[^/?#]+(?:[/?#]|$)/.test(u);
};

const getMetaValue = (meta, key) => {
  const needle = String(key || "").trim().toLowerCase();
  if (!needle) return "";
  const found = (Array.isArray(meta) ? meta : []).find(
    (row) => String(row?.key || "").trim().toLowerCase() === needle
  );
  return found?.value != null ? String(found.value) : "";
};

const getFirstMetaValue = (meta, keys) => {
  for (const key of keys) {
    const value = getMetaValue(meta, key);
    if (String(value || "").trim()) return String(value).trim();
  }
  return "";
};

const getFirstObjectValue = (objects, keys) => {
  for (const obj of objects) {
    if (!obj || typeof obj !== "object") continue;
    for (const key of keys) {
      const value = obj?.[key];
      if (value != null && String(value).trim()) return String(value).trim();
    }
  }
  return "";
};

const normalizeHost = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  try {
    const url = new URL(raw.startsWith("http") ? raw : `https://${raw}`);
    return String(url.hostname || "").toLowerCase();
  } catch {
    return raw.toLowerCase().replace(/^https?:\/\//, "").split("/")[0].split(":")[0];
  }
};

const configuredFrontendHost = normalizeHost(config?.frontendUrl || "");
const knownProductionHosts = new Set(
  ["lumen.werfklas.nl", "www.lumen.werfklas.nl", configuredFrontendHost]
    .map(normalizeHost)
    .filter((host) => host && !["localhost", "127.0.0.1", "::1"].includes(host))
    .filter((host) => !host.includes("dev") && !host.includes("development") && !host.includes("local"))
);

const collectIssueLabels = (issue) => {
  const sources = [
    issue?.labels,
    issue?.labelNames,
    issue?.label_names,
    issue?.giteaLabels,
    issue?.gitea_labels,
    issue?.issue?.labels,
  ];
  const out = [];
  sources.forEach((source) => {
    if (!source) return;
    if (Array.isArray(source)) {
      source.forEach((label) => {
        if (typeof label === "string") out.push(label);
        else if (label && typeof label === "object") {
          const value = label.name || label.title || label.label || label.value;
          if (value) out.push(value);
        }
      });
    } else if (typeof source === "object") {
      Object.values(source).forEach((value) => {
        if (value) out.push(String(value));
      });
    } else {
      out.push(String(source));
    }
  });
  return Array.from(new Set(out.map((label) => String(label || "").trim()).filter(Boolean)));
};

const badgeToneForLabel = (issue, label) => {
  const labelObject = [
    issue?.labels,
    issue?.giteaLabels,
    issue?.gitea_labels,
    issue?.issue?.labels,
  ]
    .filter(Array.isArray)
    .flat()
    .find((candidate) => candidate && typeof candidate === "object" &&
      String(candidate.name || candidate.title || candidate.label || candidate.value || "").trim() === label);
  const color = String(labelObject?.color || "").replace(/^#/, "").toLowerCase();
  if (/blocked|critical|security|geblokkeerd|kritiek/i.test(label) || ["e11d21", "b60205", "d73a4a"].includes(color)) return "danger";
  if (/priority|review|approval|medium|wacht/i.test(label) || ["fbca04", "f9d0c4", "fef2c0"].includes(color)) return "warn";
  if (/completed|done|ready|afgerond|gereed/i.test(label) || ["0e8a16", "2cbe4e", "c2e0c6"].includes(color)) return "success";
  return "neutral";
};

const normalizeEnvironmentValue = (value) => {
  const normalized = String(value || "").trim().toLowerCase();
  if (/^(dev|development|local|localhost)$/.test(normalized)) return "development";
  if (/^(prod|production|productie)$/.test(normalized)) return "production";
  return "";
};

const labelValue = (labels, prefixes) => {
  const normalizedPrefixes = prefixes.map((prefix) => String(prefix).toLowerCase());
  for (const label of labels) {
    const raw = String(label || "").trim();
    const lower = raw.toLowerCase();
    for (const prefix of normalizedPrefixes) {
      if (lower.startsWith(prefix)) {
        return raw.slice(prefix.length).trim();
      }
    }
  }
  return "";
};

const resolveIssueEnvironment = (issue) => {
  const rawBody =
    issue?.body ||
    issue?.description ||
    issue?.content ||
    "";
  const parsed = parseIssueBody(rawBody);
  const labels = collectIssueLabels(issue);
  const explicit =
    getFirstObjectValue([issue], [
      "sourceEnvironment",
      "environment",
      "env",
      "stage",
      "deploymentEnvironment",
      "appEnvironment",
    ]) ||
    labelValue(labels, ["env:", "environment:", "source-env:", "sourceEnvironment:"]) ||
    getFirstMetaValue(parsed.meta, ["Environment", "Omgeving", "Env", "Stage"]);

  const normalized = normalizeEnvironmentValue(explicit);
  if (normalized === "development") {
    return { id: "development", label: "Development" };
  }
  if (normalized === "production") {
    return { id: "production", label: "Productie" };
  }

  const pageUrl =
    issue?.pageUrl ||
    issue?.page ||
    issue?.url ||
    getFirstMetaValue(parsed.meta, ["Page", "Page URL", "URL"]);
  const host = normalizeHost(pageUrl);
  if (!host) return null;

  if (knownProductionHosts.has(host)) return { id: "production", label: "Productie" };
  if (
    host === "localhost" ||
    host === "127.0.0.1" ||
    host === "::1" ||
    host.includes("dev") ||
    host.includes("development") ||
    host.includes("local")
  ) {
    return { id: "development", label: "Development" };
  }

  return { id: "development", label: "Development" };
};

const normalizeWorkflowStatusValue = (value) => {
  const raw = String(value || "").trim();
  const normalized = raw.toLowerCase().replace(/[_-]+/g, " ");
  if (!normalized) return "";
  if (["new", "nieuw"].includes(normalized)) return "New";
  if (["in progress", "in behandeling"].includes(normalized)) return "In Progress";
  if (["on hold", "in wacht"].includes(normalized)) return "On Hold";
  if (["closed", "gesloten"].includes(normalized)) return "Closed";
  if (["rejected", "afgewezen"].includes(normalized)) return "Rejected";
  if (["done", "resolved", "opgelost"].includes(normalized)) return "Done";
  if (["blocked", "geblokkeerd"].includes(normalized)) return "Blocked";
  return raw;
};

const resolveIssueWorkflowStatus = (issue) => {
  const explicit =
    issue?.workflowStatus ??
    issue?.workflow_status ??
    issue?.workflow ??
    issue?.statusLabel ??
    null;
  const normalizedExplicit = normalizeWorkflowStatusValue(explicit);
  if (normalizedExplicit) return normalizedExplicit;

  const labels = collectIssueLabels(issue);
  const fromLabel = labelValue(labels, ["workflow:", "workflow-status:", "status:"]);
  return normalizeWorkflowStatusValue(fromLabel);
};

const environmentChipClass = (environmentId) => {
  if (environmentId === "development") {
    return "border-sky-200 bg-sky-50 text-sky-700 dark:border-sky-500/40 dark:bg-sky-950/30 dark:text-sky-200";
  }
  if (environmentId === "production") {
    return "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/40 dark:bg-emerald-950/30 dark:text-emerald-200";
  }
  return "border-slate-200 bg-slate-50 text-slate-700 dark:border-slate-500/40 dark:bg-slate-900 dark:text-slate-200";
};

const buildRequesterCandidate = ({ issue, details }) => {
  const rawBody =
    details?.body ||
    details?.description ||
    issue?.body ||
    issue?.description ||
    issue?.content ||
    "";
  const parsed = parseIssueBody(rawBody);
  const reporter = details?.reporter ?? issue?.reporter ?? null;
  const requester = details?.requester ?? details?.applicant ?? issue?.requester ?? issue?.applicant ?? null;
  const candidates = [requester, reporter, details, issue];

  const userId =
    getFirstObjectValue(candidates, [
      "userId",
      "userID",
      "user_id",
      "subject",
      "sub",
      "keycloakSubject",
      "keycloakSub",
      "requesterUserId",
      "requesterSubject",
    ]) ||
    getFirstMetaValue(parsed.meta, [
      "User ID",
      "UserId",
      "User",
      "Subject",
      "Sub",
      "Requester ID",
      "Requester User ID",
    ]);

  const email =
    getFirstObjectValue(candidates, ["email", "mail", "requesterEmail"]) ||
    formatReporterEmail(reporter) ||
    getFirstMetaValue(parsed.meta, ["Email", "E-mail", "Requester Email"]);

  const label =
    getFirstObjectValue(candidates, [
      "displayName",
      "fullName",
      "name",
      "username",
      "login",
      "requesterName",
    ]) ||
    formatReporter(reporter) ||
    email ||
    userId;

  const pageUrl = getFirstMetaValue(parsed.meta, ["Page", "Page URL", "URL"]) || details?.pageUrl || issue?.pageUrl || "";
  const issueNumber = details?.number ?? issue?.number ?? issue?.issueNumber ?? issue?.id ?? "";

  if (!userId && !email) return null;
  return {
    userId: userId || null,
    email: email || null,
    label: label || email || userId,
    issueNumber: issueNumber == null ? "" : String(issueNumber),
    source: "gitea-issue",
    pageUrl,
  };
};

const resolveViewAsTargetPath = (pageUrl) => {
  const fallback = "/portal";
  const raw = String(pageUrl || "").trim();
  if (!raw) return fallback;
  try {
    if (raw.startsWith("/")) return raw;
    if (typeof window === "undefined") return fallback;
    const url = new URL(raw, window.location.origin);
    if (url.origin === window.location.origin) return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    // ignore invalid page urls
  }
  return fallback;
};

const resolveCommentId = (comment) => {
  const value =
    comment?.id ??
    comment?._id ??
    comment?.commentId ??
    comment?.comment_id ??
    comment?.giteaCommentId ??
    comment?.gitea_comment_id ??
    comment?.issueCommentId ??
    comment?.issue_comment_id ??
    null;
  if (value == null) return "";
  return String(value).trim();
};

const getCommentBody = (comment) =>
  comment?.body ?? comment?.text ?? comment?.comment ?? comment?.message ?? "";

const splitCommentBody = (value) => {
  const normalizedBody = String(value || "");
  const internalMatch = normalizedBody.trim().match(/^\[(internal|private)\]\s*/i);
  const internalKind = internalMatch ? String(internalMatch[1] || "").toLowerCase() : "";
  const displayBody = internalMatch
    ? normalizedBody.trim().slice(internalMatch[0].length).trimStart()
    : normalizedBody;
  return {
    normalizedBody,
    internalKind,
    isInternal: Boolean(internalMatch),
    displayBody,
  };
};

const withUpdatedCommentBody = (comment, body) => {
  const next = { ...(comment || {}) };
  if ("body" in next || (!("text" in next) && !("comment" in next) && !("message" in next))) next.body = body;
  else if ("text" in next) next.text = body;
  else if ("comment" in next) next.comment = body;
  else if ("message" in next) next.message = body;
  return next;
};

export default function GiteaRecentIssues() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const supportViewAs = useSupportViewAs();
  const [statusFilter, setStatusFilter] = useState("open");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [payload, setPayload] = useState(null);
  const [reloadNonce, setReloadNonce] = useState(0);
  const [drawerIssue, setDrawerIssue] = useState(null);
  const [drawerReloadNonce, setDrawerReloadNonce] = useState(0);
  useEffect(() => {
    const issue = new URLSearchParams(location.search).get("issue");
    if (issue && /^[1-9]\d*$/.test(issue)) {
      // Fetch details directly: the linked issue need not be in the recent or filtered list.
      setDrawerIssue({ number: issue });
    }
  }, [location.search]);

  const [drawerDetails, setDrawerDetails] = useState(null);
  const [drawerDetailsLoading, setDrawerDetailsLoading] = useState(false);
  const [drawerDetailsError, setDrawerDetailsError] = useState(null);
  const [drawerComments, setDrawerComments] = useState([]);
  const [drawerCommentsLoading, setDrawerCommentsLoading] = useState(false);
  const [drawerCommentsError, setDrawerCommentsError] = useState(null);
  const [drawerDeleteCommentCandidate, setDrawerDeleteCommentCandidate] = useState(null);
  const [drawerDeletingCommentId, setDrawerDeletingCommentId] = useState(null);
  const [drawerEditCommentCandidate, setDrawerEditCommentCandidate] = useState(null);
  const [drawerEditCommentBody, setDrawerEditCommentBody] = useState("");
  const [drawerEditCommentInternal, setDrawerEditCommentInternal] = useState(false);
  const [drawerEditingCommentId, setDrawerEditingCommentId] = useState(null);
  const [drawerAssets, setDrawerAssets] = useState([]);
  const [drawerAssetsLoading, setDrawerAssetsLoading] = useState(false);
  const [drawerAssetsError, setDrawerAssetsError] = useState(null);
  const [drawerAssetObjectUrls, setDrawerAssetObjectUrls] = useState({});
  const [drawerNewComment, setDrawerNewComment] = useState("");
  const [drawerNewCommentInternal, setDrawerNewCommentInternal] = useState(false);
  const [drawerNewCommentSaving, setDrawerNewCommentSaving] = useState(false);
  const [drawerNewCommentError, setDrawerNewCommentError] = useState(null);
  const [drawerShowDefaultRoles, setDrawerShowDefaultRoles] = useState(false);
  const enrichedKeyRef = useRef("");
  const tRef = useRef(t);
  const canManageWorkflow = useCan("integration.gitea.workflow", "manage");
  const canManageIssues = useCan("integration.gitea.issues", "manage");
  const canReadComments = useCan("integration.gitea.comments", "read");
  const canNotifyApplicant = canReadComments || canManageWorkflow;
  const canUseSupportViewAsDirect = useCan("support.view-as", "use");
  const canUseSupportViewAs =
    canUseSupportViewAsDirect || canManageWorkflow || canNotifyApplicant;
  const [workflowEditorOpen, setWorkflowEditorOpen] = useState(false);
  const [workflowStatusDraft, setWorkflowStatusDraft] = useState("");
  const [workflowStatusPreset, setWorkflowStatusPreset] = useState("custom");
  const [workflowStatusSaving, setWorkflowStatusSaving] = useState(false);
  const [workflowStatusError, setWorkflowStatusError] = useState(null);
  const [notifyConfirmOpen, setNotifyConfirmOpen] = useState(false);
  const [notifySaving, setNotifySaving] = useState(false);
  const [notifyError, setNotifyError] = useState(null);
  const [notifySuccess, setNotifySuccess] = useState(null);
  const notifySendButtonRef = useRef(null);
  const notifyTriggerButtonRef = useRef(null);
  const notifyConfirmWasOpenRef = useRef(false);

  // Move focus into the inline email state and back to the trigger, so keyboard users keep their place.
  useEffect(() => {
    if (notifyConfirmOpen) {
      notifySendButtonRef.current?.focus({ preventScroll: true });
    } else if (notifyConfirmWasOpenRef.current) {
      notifyTriggerButtonRef.current?.focus({ preventScroll: true });
    }
    notifyConfirmWasOpenRef.current = notifyConfirmOpen;
  }, [notifyConfirmOpen]);

  const cancelNotifyConfirm = () => {
    setNotifyConfirmOpen(false);
    setNotifyError(null);
  };

  useEffect(() => {
    tRef.current = t;
  }, [t]);

  const normalizedStatusFilter = useMemo(
    () => String(statusFilter || "all").trim().toLowerCase(),
    [statusFilter]
  );
  const normalizedSearch = useMemo(() => String(search || "").trim().toLowerCase(), [search]);

  const configured = Boolean(payload?.configured);
  const issues = useMemo(() => {
    const list = Array.isArray(payload?.issues) ? payload.issues : [];
    return [...list].sort((a, b) => {
      const ad = parseDateTime(a?.createdAt) || parseDateTime(a?.updatedAt);
      const bd = parseDateTime(b?.createdAt) || parseDateTime(b?.updatedAt);
      const at = ad ? ad.getTime() : -Infinity;
      const bt = bd ? bd.getTime() : -Infinity;
      if (bt !== at) return bt - at; // newest first
      const an = Number(a?.number);
      const bn = Number(b?.number);
      if (Number.isFinite(bn) && Number.isFinite(an) && bn !== an) return bn - an;
      return String(b?.title || "").localeCompare(String(a?.title || ""), undefined, {
        sensitivity: "base",
      });
    });
  }, [payload]);
  const filteredIssues = useMemo(() => {
    return issues.filter((issue) => {
      const stateOk =
        normalizedStatusFilter === "all" ||
        String(issue?.state || "").trim().toLowerCase() === normalizedStatusFilter;

      if (!normalizedSearch) return stateOk;

      const haystack = [
        issue?.title,
        issue?.htmlUrl,
        issue?.number != null ? String(issue.number) : null,
        issue?.state,
        resolveIssueWorkflowStatus(issue),
        resolveIssueEnvironment(issue)?.label,
        ...collectIssueLabels(issue),
        formatReporter(issue?.reporter),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return stateOk && haystack.includes(normalizedSearch);
    });
  }, [issues, normalizedStatusFilter, normalizedSearch]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      enrichedKeyRef.current = "";
      try {
        const res = await http.get("/admin/gitea/issues/recent", {
          params: { limit: 50 },
        });
        if (cancelled) return;
        setPayload(res.data || null);
      } catch (e) {
        if (cancelled) return;
        setError(mapHttpErrorToMessage(e, tRef.current));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [reloadNonce, i18n.language]);

  useEffect(() => {
    if (!drawerIssue) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape" && !document.querySelector('[role="dialog"]')) setDrawerIssue(null);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [drawerIssue]);

  useEffect(() => {
    if (!drawerIssue) {
      setDrawerDetails(null);
      setDrawerDetailsError(null);
      setDrawerDetailsLoading(false);
      setDrawerComments([]);
      setDrawerCommentsError(null);
      setDrawerCommentsLoading(false);
      setDrawerDeleteCommentCandidate(null);
      setDrawerDeletingCommentId(null);
      setDrawerEditCommentCandidate(null);
      setDrawerEditCommentBody("");
      setDrawerEditCommentInternal(false);
      setDrawerEditingCommentId(null);
      setDrawerAssets([]);
      setDrawerAssetsError(null);
      setDrawerAssetsLoading(false);
      setDrawerAssetObjectUrls((prev) => {
        Object.values(prev || {}).forEach((u) => {
          try {
            if (u) URL.revokeObjectURL(u);
          } catch {
            // ignore
          }
        });
        return {};
      });
      setDrawerReloadNonce(0);
      setDrawerNewComment("");
      setDrawerNewCommentInternal(false);
      setDrawerNewCommentError(null);
      setDrawerNewCommentSaving(false);
      setDrawerShowDefaultRoles(false);
      setWorkflowEditorOpen(false);
      setWorkflowStatusDraft("");
      setWorkflowStatusPreset("custom");
      setWorkflowStatusError(null);
      setWorkflowStatusSaving(false);
      setNotifyConfirmOpen(false);
      setNotifySaving(false);
      setNotifyError(null);
      setNotifySuccess(null);
      return;
    }
    let cancelled = false;
    const loadDetails = async () => {
      const number = drawerIssue?.number ?? drawerIssue?.issueNumber ?? drawerIssue?.id ?? null;
      if (number == null) return;
      setDrawerDetails(null);
      setDrawerDetailsError(null);
      setDrawerDetailsLoading(true);
      try {
        const res = await http.get(`/issues/${encodeURIComponent(number)}`);
        if (cancelled) return;
        setDrawerDetails(res?.data || null);
      } catch (e) {
        if (cancelled) return;
        setDrawerDetails(null);
        setDrawerDetailsError(mapHttpErrorToMessage(e, t) || e);
      } finally {
        if (!cancelled) setDrawerDetailsLoading(false);
      }
    };
    loadDetails();
    return () => {
      cancelled = true;
    };
  }, [drawerIssue, drawerReloadNonce, t]);

  const drawerBaseSource = useMemo(
    () => mergeIssueData(drawerIssue, drawerDetails),
    [drawerIssue, drawerDetails]
  );
  const drawerSource = drawerBaseSource;

  const drawerWorkflowStatus = useMemo(() => {
    return resolveIssueWorkflowStatus(drawerSource) || "";
  }, [drawerSource]);

  useEffect(() => {
    const current = drawerWorkflowStatus;
    setWorkflowStatusDraft(current || "");
    if (!current) {
      setWorkflowStatusPreset("custom");
    } else {
      const normalized = String(current).trim().toLowerCase();
      const preset =
        normalized === "new"
          ? "New"
          : normalized === "in progress" || normalized === "in behandeling"
            ? "In Progress"
          : normalized === "on hold" || normalized === "in wacht"
            ? "On Hold"
            : normalized === "closed" || normalized === "gesloten"
              ? "Closed"
              : normalized === "rejected" || normalized === "afgewezen"
                ? "Rejected"
                : "custom";
      setWorkflowStatusPreset(preset);
    }
    setWorkflowEditorOpen(false);
  }, [drawerWorkflowStatus]);

  const drawerIssueNumber = useMemo(() => {
    const number = drawerDetails?.number ?? drawerIssue?.number ?? drawerIssue?.issueNumber ?? drawerIssue?.id ?? null;
    return number == null ? null : String(number);
  }, [drawerDetails, drawerIssue]);

  const drawerRequester = useMemo(
    () => buildRequesterCandidate({ issue: drawerIssue, details: drawerDetails }),
    [drawerIssue, drawerDetails]
  );
  const startRequesterViewAs = () => {
    if (!drawerRequester) return;
    const started = supportViewAs.start(drawerRequester);
    if (!started) return;
    setDrawerIssue(null);
    navigate(resolveViewAsTargetPath(drawerRequester.pageUrl));
  };
  const defaultNotifyLabel = useMemo(() => {
    if (!drawerIssueNumber) return t("gitea.notify.default_label", { defaultValue: "New message about your report" });
    return t("gitea.notify.default_label_with_nr", {
      defaultValue: `New message about your report #${drawerIssueNumber}`,
      nr: drawerIssueNumber,
    });
  }, [drawerIssueNumber, t]);
  const defaultNotifyTargetUrl = useMemo(() => {
    if (!drawerIssueNumber) return "/issues";
    return `/issues/${drawerIssueNumber}`;
  }, [drawerIssueNumber]);

  const submitDrawerComment = async () => {
    const number = drawerIssueNumber;
    const body = String(drawerNewComment || "").trim();
    if (!number || !body) return;
    const visibility = drawerNewCommentInternal ? "internal" : "public";
    const submittedBody = drawerNewCommentInternal && !/^\[(internal|private)\]\s*/i.test(body)
      ? `[internal] ${body}`
      : body;
    setDrawerNewCommentSaving(true);
    setDrawerNewCommentError(null);
    try {
      const endpoints = [
        `/admin/gitea/issues/${encodeURIComponent(number)}/comments`,
        `/issues/${encodeURIComponent(number)}/comments`,
      ];
      let lastErr = null;
      for (const url of endpoints) {
        try {
          await http.post(url, {
            body: submittedBody,
            visibility,
            internal: drawerNewCommentInternal,
          });
          lastErr = null;
          break;
        } catch (e) {
          lastErr = e;
        }
      }
      if (lastErr) throw lastErr;
      setDrawerNewComment("");
      setDrawerNewCommentInternal(false);
      setDrawerReloadNonce((n) => n + 1);
    } catch (e) {
      setDrawerNewCommentError(mapHttpErrorToMessage(e, t) || e);
    } finally {
      setDrawerNewCommentSaving(false);
    }
  };

  const startDrawerCommentEdit = (comment) => {
    const commentId = resolveCommentId(comment);
    if (!commentId) return;
    const { displayBody, isInternal } = splitCommentBody(getCommentBody(comment));
    setDrawerDeleteCommentCandidate(null);
    setDrawerEditCommentCandidate(comment);
    setDrawerEditCommentBody(displayBody);
    setDrawerEditCommentInternal(isInternal);
    setDrawerCommentsError(null);
  };

  const cancelDrawerCommentEdit = () => {
    setDrawerEditCommentCandidate(null);
    setDrawerEditCommentBody("");
    setDrawerEditCommentInternal(false);
  };

  const saveDrawerCommentEdit = async (comment) => {
    const number = drawerIssueNumber;
    const commentId = resolveCommentId(comment);
    const body = String(drawerEditCommentBody || "").trim();
    if (!number || !commentId || !body) return;
    const visibility = drawerEditCommentInternal ? "internal" : "public";
    const submittedBody = drawerEditCommentInternal && !/^\[(internal|private)\]\s*/i.test(body)
      ? `[internal] ${body}`
      : body;

    setDrawerEditingCommentId(commentId);
    setDrawerCommentsError(null);
    try {
      const endpoints = [
        `/admin/gitea/issues/${encodeURIComponent(number)}/comments/${encodeURIComponent(commentId)}`,
        `/issues/${encodeURIComponent(number)}/comments/${encodeURIComponent(commentId)}`,
      ];
      const payload = {
        body: submittedBody,
        visibility,
        internal: drawerEditCommentInternal,
      };
      let lastErr = null;
      for (const url of endpoints) {
        try {
          await http.patch(url, payload);
          lastErr = null;
          break;
        } catch (e) {
          lastErr = e;
          try {
            await http.put(url, payload);
            lastErr = null;
            break;
          } catch (putErr) {
            lastErr = putErr;
          }
        }
      }
      if (lastErr) throw lastErr;
      setDrawerComments((prev) =>
        (Array.isArray(prev) ? prev : []).map((item) =>
          resolveCommentId(item) === commentId ? withUpdatedCommentBody(item, submittedBody) : item
        )
      );
      cancelDrawerCommentEdit();
      setDrawerReloadNonce((n) => n + 1);
    } catch (e) {
      setDrawerCommentsError(mapHttpErrorToMessage(e, t) || e);
    } finally {
      setDrawerEditingCommentId(null);
    }
  };

  const deleteDrawerComment = async (comment) => {
    const number = drawerIssueNumber;
    const commentId = resolveCommentId(comment);
    if (!number || !commentId) return;

    setDrawerDeletingCommentId(commentId);
    setDrawerCommentsError(null);
    try {
      const endpoints = [
        `/admin/gitea/issues/${encodeURIComponent(number)}/comments/${encodeURIComponent(commentId)}`,
        `/issues/${encodeURIComponent(number)}/comments/${encodeURIComponent(commentId)}`,
      ];
      let lastErr = null;
      for (const url of endpoints) {
        try {
          await http.delete(url);
          lastErr = null;
          break;
        } catch (e) {
          lastErr = e;
        }
      }
      if (lastErr) throw lastErr;
      setDrawerComments((prev) =>
        (Array.isArray(prev) ? prev : []).filter((item) => resolveCommentId(item) !== commentId)
      );
      setDrawerDeleteCommentCandidate(null);
      setDrawerReloadNonce((n) => n + 1);
    } catch (e) {
      setDrawerCommentsError(mapHttpErrorToMessage(e, t) || e);
    } finally {
      setDrawerDeletingCommentId(null);
    }
  };

  const openDrawerAsset = async (url) => {
    const u = String(url || "").trim();
    if (!u) return;
    const existing = drawerAssetObjectUrls?.[u];
    if (existing) {
      window.open(existing, "_blank") || window.open(existing, "_self");
      return;
    }
    // Open a blank tab synchronously (user gesture) to avoid popup blockers,
    // then navigate it to the blob URL once the authenticated fetch completes.
    const popup = window.open("", "_blank");
    try {
      if (popup?.document) {
        popup.document.title = "Bijlage laden…";
        popup.document.body.innerHTML =
          '<div style="font-family:system-ui,-apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif;padding:24px;color:#0f172a">' +
          "<h2 style=\"margin:0 0 8px;font-size:18px\">Bijlage laden…</h2>" +
          "<p style=\"margin:0;font-size:14px;color:#475569\">Even geduld.</p>" +
          "</div>";
      }
    } catch {
      // ignore
    }
    try {
      const res = await http.get(u, { responseType: "blob" });
      const blob = res?.data;
      if (!blob) return;
      const objectUrl = URL.createObjectURL(blob);
      setDrawerAssetObjectUrls((prev) => ({ ...(prev || {}), [u]: objectUrl }));
      if (popup && !popup.closed) {
        popup.location.href = objectUrl;
      } else {
        window.open(objectUrl, "_self");
      }
    } catch (e) {
      const rawMessage = mapHttpErrorToMessage(e, tRef.current) || "Kon bijlage niet laden.";
      const parts = [];
      if (typeof rawMessage === "string") parts.push(rawMessage);
      else {
        try {
          parts.push(JSON.stringify(rawMessage, null, 2));
        } catch {
          parts.push(String(rawMessage));
        }
      }
      try {
        const status = e?.response?.status;
        if (status) parts.push(`HTTP ${status}`);
        const data = e?.response?.data;
        if (data != null && typeof data !== "string") parts.push(JSON.stringify(data, null, 2));
        else if (typeof data === "string" && data.trim()) parts.push(data.trim());
      } catch {
        // ignore
      }
      const message = parts.filter(Boolean).join("\n\n");
      try {
        if (popup && !popup.closed && popup.document) {
          popup.document.title = "Bijlage niet beschikbaar";
          popup.document.body.innerHTML =
            '<div style="font-family:system-ui,-apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif;padding:24px;color:#0f172a">' +
            "<h2 style=\"margin:0 0 8px;font-size:18px\">Bijlage niet beschikbaar</h2>" +
            `<pre style="white-space:pre-wrap;margin:0;font-size:13px;color:#b91c1c">${String(message || "").replace(
              /</g,
              "&lt;"
            )}</pre>` +
            "</div>";
        }
      } catch {
        // ignore
      }
    }
  };

  const submitWorkflowStatus = async (nextStatus = null) => {
    const number = drawerIssueNumber;
    const status = String(nextStatus || workflowStatusDraft || "").trim();
    if (!number || !status) return;
    setWorkflowStatusSaving(true);
    setWorkflowStatusError(null);
    try {
      await http.put(`/admin/gitea/issues/${encodeURIComponent(number)}/workflow-status`, { status });
      setReloadNonce((n) => n + 1);
      if (isClosedWorkflowStatus(status)) {
        setDrawerIssue(null);
      } else {
        setDrawerReloadNonce((n) => n + 1);
        setWorkflowEditorOpen(false);
      }
    } catch (e) {
      setWorkflowStatusError(mapHttpErrorToMessage(e, tRef.current) || e);
    } finally {
      setWorkflowStatusSaving(false);
    }
  };

  const submitNotifyApplicant = async () => {
    const number = drawerIssueNumber;
    if (!number) return;
    setNotifySaving(true);
    setNotifyError(null);
    setNotifySuccess(null);
    try {
      const label = defaultNotifyLabel;
      const targetUrl = defaultNotifyTargetUrl;
      const requesterPayload = drawerRequester
        ? {
            reporterSub: drawerRequester.userId || null,
            requesterSub: drawerRequester.userId || null,
            userId: drawerRequester.userId || null,
            email: drawerRequester.email || null,
            requesterEmail: drawerRequester.email || null,
            requesterLabel: drawerRequester.label || null,
          }
        : {};
      const hasBody = Boolean(label || targetUrl || Object.keys(requesterPayload).length);
      let res = null;
      if (hasBody) {
        const body = { ...requesterPayload };
        if (label) body.label = label;
        if (targetUrl) body.targetUrl = targetUrl;
        res = await http.post(`/admin/gitea/issues/${encodeURIComponent(number)}/notify`, body);
      } else {
        res = await http.post(`/admin/gitea/issues/${encodeURIComponent(number)}/notify`);
      }
      if (res?.data && typeof res.data === "object" && res.data?.success === false) {
        const message =
          typeof res.data?.message === "string" && res.data.message.trim()
            ? res.data.message.trim()
            : t("gitea.notify.failed", { defaultValue: "Failed to send email." });
        setNotifyError(message);
        return;
      }
      const usedLabel = label || defaultNotifyLabel;
      const usedTargetUrl = targetUrl || defaultNotifyTargetUrl;
      let noteOk = true;
      try {
        const note = `[internal] e-mail verstuurd naar aanvrager (${usedLabel} → ${usedTargetUrl}).`;
        await http.post(`/admin/gitea/issues/${encodeURIComponent(number)}/comments`, { body: note });
      } catch (e) {
        noteOk = false;
      }
      setNotifySuccess(
        noteOk
          ? t("gitea.notify.sent", { defaultValue: "Email sent to the reporter." })
          : t("gitea.notify.sent_note_failed", {
              defaultValue: "Email sent (failed to add internal note).",
            })
      );
      window.setTimeout(() => {
        setNotifyConfirmOpen(false);
        setNotifySuccess(null);
      }, 1200);
    } catch (e) {
      setNotifyError(
        extractApiErrorMessage(e) ||
          mapHttpErrorToMessage(e, tRef.current) ||
          t("gitea.notify.failed", { defaultValue: "Failed to send email." })
      );
    } finally {
      setNotifySaving(false);
    }
  };

  const extractComments = (data) => {
    if (!data) return [];
    if (Array.isArray(data)) return data;
    const candidates = [data.comments, data.items, data.entries, data.content].filter(Array.isArray);
    if (candidates.length > 0) return candidates[0];
    if (data.issue && (Array.isArray(data.issue.comments) || Array.isArray(data.issue.items))) {
      return data.issue.comments || data.issue.items;
    }
    return [];
  };

  useEffect(() => {
    let cancelled = false;
    const loadComments = async () => {
      const number = drawerIssue?.number ?? drawerIssue?.issueNumber ?? drawerIssue?.id ?? null;
      if (number == null) return;
      setDrawerComments([]);
      setDrawerCommentsError(null);
      setDrawerDeleteCommentCandidate(null);
      setDrawerEditCommentCandidate(null);
      setDrawerEditCommentBody("");
      setDrawerEditCommentInternal(false);
      setDrawerCommentsLoading(true);
      try {
        const endpoints = [
          `/admin/gitea/issues/${encodeURIComponent(number)}/comments`,
          `/issues/${encodeURIComponent(number)}/comments`,
          `/issues/${encodeURIComponent(number)}`,
        ];
        let data = null;
        let lastErr = null;
        for (const url of endpoints) {
          try {
            const res = await http.get(url);
            data = res?.data;
            lastErr = null;
            break;
          } catch (e) {
            lastErr = e;
          }
        }
        if (lastErr) throw lastErr;
        const comments = extractComments(data);
        if (cancelled) return;
        setDrawerComments(comments);
      } catch (e) {
        if (cancelled) return;
        setDrawerComments([]);
        setDrawerCommentsError(mapHttpErrorToMessage(e, t) || e);
      } finally {
        if (!cancelled) setDrawerCommentsLoading(false);
      }
    };

    if (drawerIssue) loadComments();
    return () => {
      cancelled = true;
    };
  }, [drawerIssue, drawerReloadNonce, t]);

  useEffect(() => {
    let cancelled = false;
    const loadAssets = async () => {
      const number = drawerIssueNumber;
      if (!number) return;
      setDrawerAssets([]);
      setDrawerAssetsError(null);
      setDrawerAssetsLoading(true);
      setDrawerAssetObjectUrls((prev) => {
        Object.values(prev || {}).forEach((u) => {
          try {
            if (u) URL.revokeObjectURL(u);
          } catch {
            // ignore
          }
        });
        return {};
      });
      try {
        const res = await http.get(`/issues/${encodeURIComponent(number)}/assets`);
        const assets = normalizeAssets(res?.data);
        if (cancelled) return;
        setDrawerAssets(assets);
      } catch (e) {
        if (cancelled) return;
        setDrawerAssets([]);
        setDrawerAssetsError(mapHttpErrorToMessage(e, t) || e);
      } finally {
        if (!cancelled) setDrawerAssetsLoading(false);
      }
    };

    if (drawerIssueNumber) loadAssets();
    return () => {
      cancelled = true;
    };
  }, [drawerIssueNumber, drawerReloadNonce, t]);

  const resolvedDrawerAssets = useMemo(() => {
    const baseURL = http?.defaults?.baseURL || "";
    return (Array.isArray(drawerAssets) ? drawerAssets : [])
      .map(assetDisplay)
      .map((a) => {
        const proxyUrl = buildProxyAssetUrl(baseURL, drawerIssueNumber, a.id);
        const resolvedUrl = proxyUrl || a.url || "";
        return { ...a, resolvedUrl };
      })
      .filter((a) => a.resolvedUrl);
  }, [drawerAssets, drawerIssueNumber, http?.defaults?.baseURL]);

  const drawerImageAssets = useMemo(() => {
    return resolvedDrawerAssets.filter((a) => isImageAsset(a));
  }, [resolvedDrawerAssets]);

  const drawerOtherAssets = useMemo(() => {
    return resolvedDrawerAssets.filter((a) => !isImageAsset(a));
  }, [resolvedDrawerAssets]);

  useEffect(() => {
    if (drawerImageAssets.length === 0) return;
    let cancelled = false;
    const toFetch = drawerImageAssets
      .map((a) => a.resolvedUrl)
      .filter((u) => u && !String(u).startsWith("data:"))
      .filter((u) => !drawerAssetObjectUrls?.[u]);
    if (toFetch.length === 0) return;

    const concurrency = 3;
    let idx = 0;
    const workers = Array.from({ length: Math.min(concurrency, toFetch.length) }).map(async () => {
      while (idx < toFetch.length && !cancelled) {
        const url = toFetch[idx++];
        try {
          const res = await http.get(url, { responseType: "blob" });
          const blob = res?.data;
          if (!blob) continue;
          const objectUrl = URL.createObjectURL(blob);
          if (cancelled) {
            try {
              URL.revokeObjectURL(objectUrl);
            } catch {
              // ignore
            }
            continue;
          }
          setDrawerAssetObjectUrls((prev) => ({ ...(prev || {}), [url]: objectUrl }));
        } catch {
          // ignore
        }
      }
    });

    Promise.all(workers);
    return () => {
      cancelled = true;
    };
  }, [drawerImageAssets, drawerAssetObjectUrls]);

  const sortedDrawerComments = useMemo(() => {
    const list = Array.isArray(drawerComments) ? drawerComments : [];
    return [...list].sort((a, b) => {
      const ad = parseDateTime(a?.createdAt ?? a?.created_at ?? a?.created ?? a?.timestamp ?? null);
      const bd = parseDateTime(b?.createdAt ?? b?.created_at ?? b?.created ?? b?.timestamp ?? null);
      const at = ad ? ad.getTime() : Infinity;
      const bt = bd ? bd.getTime() : Infinity;
      if (at !== bt) return at - bt; // oldest first
      return String(a?.id ?? "").localeCompare(String(b?.id ?? ""), undefined, { numeric: true });
    });
  }, [drawerComments]);

  if (error) {
    return (
      <ErrorPage
        message={error}
        status={error?.status}
        onRetry={() => setReloadNonce((n) => n + 1)}
      />
    );
  }

  return (
    <LdsOverviewPage>
      <LdsOverviewHero
        icon={FaGitAlt}
        eyebrow={t("gitea.eyebrow", { defaultValue: "Integraties" })}
        title="Gitea"
        description={configured ? "Gitea is geconfigureerd." : "Gitea is niet geconfigureerd."}
        actions={
          <div className="flex flex-col items-start gap-3">
            <div className="flex flex-wrap items-center gap-3">
            <GiteaStatusDot />
            <span className="inline-flex items-center rounded-full bg-white/80 px-4 py-2 text-sm font-semibold text-indigo-700 shadow-sm ring-1 ring-indigo-100 dark:bg-indigo-950/30 dark:text-indigo-100 dark:ring-indigo-500/30">
              {filteredIssues.length} issues
            </span>
            </div>
            {canManageIssues && configured && <GiteaCreateIssue onCreated={() => setReloadNonce((n) => n + 1)} />}
          </div>
        }
      />

      <div className="space-y-6 text-gray-900 dark:text-gray-100">
        <div className="rounded-[24px] border border-slate-200/80 bg-white/95 p-5 shadow-sm dark:border-white/10 dark:bg-slate-900/80">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <h2 className="text-xl font-semibold text-slate-950 dark:text-white">
                Recente issues
              </h2>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                Filter meldingen uit Gitea op status, titel, nummer of reporter.
              </p>
            </div>

            <div className="flex flex-wrap items-end gap-3">
              <label className="flex flex-col gap-1">
                <span className="font-medium text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  Status
                </span>
                <select
                  id="gitea-status"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="lumen-glow-input border-none bg-transparent px-3 py-2 text-sm text-gray-900 dark:text-white"
                >
                  <option value="all">All</option>
                  <option value="open">Open</option>
                  <option value="closed">Closed</option>
                </select>
              </label>

              <label className="flex flex-col gap-1">
                <span className="font-medium text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  Search
                </span>
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="title / # / url…"
                  className="lumen-glow-input border-none bg-transparent px-3 py-2 text-sm text-gray-900 dark:text-white"
                />
              </label>

              <button
                type="button"
                onClick={() => setReloadNonce((n) => n + 1)}
                disabled={loading}
                className="lumen-glow-button text-sm disabled:opacity-60"
              >
                {loading ? t("common.loading", { defaultValue: "Laden…" }) : t("common.refresh", { defaultValue: "Vernieuwen" })}
              </button>
            </div>
          </div>
        </div>

        <LdsTableCard>
          {loading ? (
            <div className="p-8">
              <Spinner />
            </div>
          ) : !configured ? (
            <div className="p-6 text-sm text-slate-700 dark:text-slate-200">
              Configureer eerst Gitea in de backend om issues te tonen.
            </div>
          ) : filteredIssues.length === 0 ? (
            <div className="p-6 text-sm text-slate-700 dark:text-slate-200">
              Geen issues gevonden.
            </div>
          ) : (
            <LdsTableScroll>
                <LdsTable>
                  <LdsTableHead>
                    <LdsTableRow>
                      <LdsTableHeadCell>
                        #
                      </LdsTableHeadCell>
                      <LdsTableHeadCell>
                        Titel
                      </LdsTableHeadCell>
                      <LdsTableHeadCell>
                        Reporter
                      </LdsTableHeadCell>
                      <LdsTableHeadCell>
                        Status
                      </LdsTableHeadCell>
                      <LdsTableHeadCell>
                        Aangemaakt
                      </LdsTableHeadCell>
                      <LdsTableHeadCell>
                        Geüpdatet
                      </LdsTableHeadCell>
                    </LdsTableRow>
                  </LdsTableHead>
                  <LdsTableBody>
                    {filteredIssues.map((issue) => {
                      const environment = resolveIssueEnvironment(issue);
                      const workflowStatus = resolveIssueWorkflowStatus(issue);
                      const issueKind = resolveIssueKind(issue);
                      const issueTypeDisplay = resolveIssueTypeDisplay(issue);
                      const stale = isOlderThanDays(issue?.updatedAt, 14);
                      return (
                        <LdsTableRow
                          key={`${issue?.number}-${issue?.htmlUrl || ""}`}
                          className={
                            stale
                              ? "cursor-pointer !bg-amber-50/80 hover:!bg-amber-100/80 dark:!bg-amber-950/20 dark:hover:!bg-amber-900/30"
                              : "cursor-pointer"
                          }
                          onClick={() => setDrawerIssue(issue)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") setDrawerIssue(issue);
                          }}
                        >
                          <LdsTableCell className="font-medium text-gray-900 dark:text-gray-100">
                            {issue?.number ?? "-"}
                          </LdsTableCell>
                          <LdsTableCell className="text-gray-800 dark:text-gray-100">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="font-medium text-indigo-700 dark:text-indigo-300">
                                {issue?.title || "-"}
                              </span>
                              {environment && (
                                <span
                                  className={`inline-flex shrink-0 rounded-full border px-2.5 py-1 text-xs font-semibold ${environmentChipClass(
                                    environment.id
                                  )}`}
                                >
                                  {environment.label}
                                </span>
                              )}
                            </div>
                          </LdsTableCell>
                          <LdsTableCell className="text-gray-800 dark:text-gray-100">
                            {issueKind === "development"
                              ? issueTypeDisplay || t("gitea.development.type", { defaultValue: "Development issue" })
                              : formatReporter(issue?.reporter)}
                          </LdsTableCell>
                          <LdsTableCell>
                            {workflowStatus ? (
                              <span
                                className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${workflowChipClass(
                                  workflowStatus
                                )}`}
                              >
                                {workflowStatusLabel(workflowStatus, t)}
                              </span>
                            ) : (
                              <span
                                className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${stateChipClass(
                                  issue?.state
                                )}`}
                              >
                                {issue?.state ?? "-"}
                              </span>
                            )}
                          </LdsTableCell>
                          <LdsTableCell className="text-gray-800 dark:text-gray-100">
                            {formatDateTime(issue?.createdAt)}
                          </LdsTableCell>
                          <LdsTableCell className="text-gray-800 dark:text-gray-100">
                            {formatDateTime(issue?.updatedAt)}
                          </LdsTableCell>
                        </LdsTableRow>
                      );
                    })}
                  </LdsTableBody>
                </LdsTable>
            </LdsTableScroll>
          )}
        </LdsTableCard>
      </div>

      {/* Drawer */}
      {drawerIssue && (
        <div className="fixed inset-0 z-[9999]">
          <button
            type="button"
            className="absolute inset-0 bg-slate-950/40 backdrop-blur-[1px]"
            onClick={() => setDrawerIssue(null)}
            aria-label={t("common.close", { defaultValue: "Close" })}
          />
          <aside className={`${resolveGiteaDrawerClass(drawerSource)} !fixed !right-0 !left-auto !top-0 !bottom-0 !h-dvh !max-h-none !rounded-none relative border-l border-slate-300 bg-white/95 dark:border-slate-700 dark:bg-slate-950/95`}>
            <div
              className={`relative px-4 pb-5 pt-4 transition-[background-color,box-shadow] duration-200 ${
                notifyConfirmOpen
                  ? "bg-indigo-50/60 shadow-[inset_2px_0_0_rgba(99,102,241,0.55)] dark:bg-indigo-500/[0.06]"
                  : ""
              }`}
            >
              <div className="min-w-0">
                {notifyConfirmOpen ? (
                  <div
                    role="group"
                    aria-labelledby="gitea-notify-inline-title"
                    className="lumen-inline-swap"
                    onKeyDown={(e) => {
                      if (e.key !== "Escape") return;
                      // Escape leaves the inline email state instead of closing the whole drawer.
                      e.stopPropagation();
                      if (!notifySaving) cancelNotifyConfirm();
                    }}
                  >
                    <div
                      id="gitea-notify-inline-title"
                      className={`flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide ${
                        notifySuccess ? "text-emerald-700 dark:text-emerald-300" : "text-indigo-700 dark:text-indigo-300"
                      }`}
                    >
                      {notifySuccess ? (
                        <FaCheck className="h-3 w-3" aria-hidden="true" />
                      ) : (
                        <FaEnvelope className="h-3 w-3" aria-hidden="true" />
                      )}
                      <span>
                        {notifySuccess
                          ? t("gitea.notify.sent_title", { defaultValue: "Email sent" })
                          : t("gitea.notify.confirm_title", { defaultValue: "Send email" })}
                      </span>
                    </div>
                    <h3 className="mt-1 truncate text-lg font-semibold text-gray-900 dark:text-gray-100">
                      {((drawerDetails?.number ?? drawerIssue?.number) ?? "-") !== "-"
                        ? `#${drawerDetails?.number ?? drawerIssue?.number}`
                        : "#-"}{" "}
                      {drawerDetails?.title || drawerIssue?.title || ""}
                    </h3>
                    <div className="mt-1.5 flex min-w-0 items-baseline gap-2 text-sm">
                      <span className="shrink-0 text-gray-500 dark:text-gray-400">
                        {t("gitea.notify.to", { defaultValue: "To" })}
                      </span>
                      <span className="min-w-0 truncate font-semibold text-gray-900 dark:text-gray-100">
                        {drawerRequester?.email ||
                          drawerRequester?.label ||
                          t("gitea.notify.reporter_fallback", { defaultValue: "the reporter" })}
                      </span>
                    </div>
                    <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                      {t("gitea.notify.confirm_text", {
                        defaultValue: "A standard email will be sent to the reporter by Lumen.",
                      })}
                    </p>
                    <div className="mt-2.5 flex min-h-8 flex-wrap items-center gap-x-1 gap-y-2">
                      {notifySuccess ? (
                        <p
                          role="status"
                          className="inline-flex items-center gap-1.5 text-sm font-medium text-emerald-700 dark:text-emerald-300"
                        >
                          <FaCheck className="h-3 w-3 shrink-0" aria-hidden="true" />
                          <span>{notifySuccess}</span>
                        </p>
                      ) : (
                        <>
                          <button
                            ref={notifySendButtonRef}
                            type="button"
                            onClick={submitNotifyApplicant}
                            disabled={notifySaving || !drawerIssueNumber}
                            aria-busy={notifySaving || undefined}
                            className="inline-flex h-8 items-center gap-2 rounded-lg bg-indigo-600 px-3.5 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400/60 focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-indigo-500 dark:hover:bg-indigo-400 dark:focus-visible:ring-offset-slate-950"
                          >
                            {notifySaving ? (
                              <span
                                className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/40 border-t-white"
                                aria-hidden="true"
                              />
                            ) : (
                              <FaPaperPlane className="h-3 w-3" aria-hidden="true" />
                            )}
                            <span>
                              {notifySaving
                                ? t("common.sending", { defaultValue: "Sending…" })
                                : t("gitea.notify.send", { defaultValue: "Send" })}
                            </span>
                          </button>
                          <button
                            type="button"
                            onClick={cancelNotifyConfirm}
                            disabled={notifySaving}
                            className="inline-flex h-8 items-center gap-1.5 rounded-lg px-2.5 text-sm font-medium text-gray-600 transition hover:bg-slate-500/10 hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400/60 disabled:cursor-not-allowed disabled:opacity-50 dark:text-gray-300 dark:hover:text-gray-100"
                          >
                            <span>{t("common.cancel", { defaultValue: "Cancel" })}</span>
                            <kbd
                              className="rounded border border-slate-300 px-1 font-sans text-[10px] font-medium leading-4 text-gray-500 dark:border-slate-600 dark:text-gray-400"
                              aria-hidden="true"
                            >
                              Esc
                            </kbd>
                          </button>
                        </>
                      )}
                      {notifyError && !notifySuccess && (
                        <span
                          role="alert"
                          className="ml-1 inline-flex min-w-0 max-w-full items-center gap-1.5 text-xs font-medium text-rose-700 dark:text-rose-300"
                        >
                          <FaExclamationCircle className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                          <span className="min-w-0 truncate">
                            {notifyError?.message || notifyError?.title || String(notifyError)}
                          </span>
                        </span>
                      )}
                    </div>
                  </div>
                ) : (
                  <>
                <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  Issue
                </div>
                <h3 className="mt-1 text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {((drawerDetails?.number ?? drawerIssue?.number) ?? "-") !== "-"
                    ? `#${drawerDetails?.number ?? drawerIssue?.number}`
                    : "#-"}{" "}
                  {drawerDetails?.title || drawerIssue?.title || ""}
                </h3>
                <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-gray-600 dark:text-gray-300">
                  <span
                    className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${stateChipClass(
                      drawerDetails?.state ?? drawerIssue?.state
                    )}`}
                  >
                    {drawerDetails?.state ?? drawerIssue?.state ?? "-"}
                  </span>
                  {(() => {
                    const current = drawerWorkflowStatus;
                    if (current) {
                      if (canManageWorkflow) {
                        return (
                          <span className="relative inline-flex">
                            <button
                              type="button"
                              onClick={() => {
                                setWorkflowStatusError(null);
                                setWorkflowEditorOpen((v) => !v);
                              }}
                              className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold transition hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-indigo-400/60 ${workflowChipClass(
                                current
                              )}`}
                              aria-expanded={workflowEditorOpen}
                              aria-label={t("gitea.workflow_status.edit", { defaultValue: "Change workflow status" })}
                            >
                              {workflowStatusLabel(current, t)}
                            </button>
                            {workflowEditorOpen && (
                              <>
                                <button
                                  type="button"
                                  className="fixed inset-0 z-[10010] cursor-default"
                                  onClick={() => {
                                    setWorkflowEditorOpen(false);
                                    setWorkflowStatusError(null);
                                  }}
                                  aria-label={t("common.close", { defaultValue: "Close" })}
                                />
                                <div className="absolute left-0 top-full z-[10020] mt-2 w-72 rounded-2xl border border-slate-200 bg-white p-3 shadow-2xl dark:border-slate-700 dark:bg-slate-950">
                                  <div className="px-1 pb-2 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                                    {t("gitea.workflow_status.edit", { defaultValue: "Change status" })}
                                  </div>
                                  <div className="space-y-1">
                                    {WORKFLOW_STATUS_PRESETS.map((preset) => {
                                      const selected = workflowStatusDraft === preset.value;
                                      return (
                                        <button
                                          key={preset.value}
                                          type="button"
                                          onClick={() => {
                                            if (workflowStatusSaving || selected) return;
                                            setWorkflowStatusPreset(preset.value);
                                            setWorkflowStatusDraft(preset.value);
                                            submitWorkflowStatus(preset.value);
                                          }}
                                          disabled={workflowStatusSaving}
                                          className={`flex w-full items-center justify-between rounded-xl px-3 py-2 text-left text-sm font-semibold transition ${
                                            selected
                                              ? "bg-indigo-50 text-indigo-900 dark:bg-indigo-950/50 dark:text-indigo-100"
                                              : "text-slate-700 hover:bg-slate-50 dark:text-slate-100 dark:hover:bg-slate-900"
                                          }`}
                                        >
                                          <span>{t(preset.labelKey, { defaultValue: preset.defaultValue })}</span>
                                          {selected && <span className="text-indigo-600 dark:text-indigo-300">✓</span>}
                                        </button>
                                      );
                                    })}
                                  </div>
                                  {workflowStatusError && (
                                    <p className="mt-2 px-1 text-xs text-red-600 dark:text-red-400">
                                      {workflowStatusError?.message || workflowStatusError?.title || String(workflowStatusError)}
                                    </p>
                                  )}
                                  {workflowStatusSaving && (
                                    <div className="mt-2 px-1 text-xs font-medium text-slate-500 dark:text-slate-400">
                                      {t("common.saving", { defaultValue: "Saving…" })}
                                    </div>
                                  )}
                                </div>
                              </>
                            )}
                          </span>
                        );
                      }
                      return (
                        <span
                          className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${workflowChipClass(
                            current
                          )}`}
                        >
                          {workflowStatusLabel(current, t)}
                        </span>
                      );
                    }

                    if (!canManageWorkflow) return null;
                    return (
                      <button
                        type="button"
                        onClick={() => setWorkflowEditorOpen((v) => !v)}
                        className="inline-flex rounded-full px-3 py-1 text-xs font-semibold bg-slate-500/10 text-slate-700 transition hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-indigo-400/60 dark:text-slate-200"
                        aria-expanded={workflowEditorOpen}
                        aria-label={t("gitea.workflow_status.edit", { defaultValue: "Workflow status wijzigen" })}
                      >
                        {t("gitea.workflow_status.set", { defaultValue: "Status instellen" })}
                      </button>
                    );
                  })()}
                  <span>•</span>
                  <span>
                    {t("common.created", { defaultValue: "Created" })}:{" "}
                    {formatDateTime(drawerDetails?.createdAt ?? drawerIssue?.createdAt)}
                  </span>
                  <span>•</span>
                  <span>
                    {t("common.updated", { defaultValue: "Updated" })}:{" "}
                    {formatDateTime(drawerDetails?.updatedAt ?? drawerIssue?.updatedAt)}
                  </span>
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-2 text-sm text-gray-600 dark:text-gray-300">
                  <span>
                    {t("common.reporter", { defaultValue: "Reporter" })}:{" "}
                    {(() => {
                      const reporter = drawerDetails?.reporter ?? drawerIssue?.reporter;
                      const rawBody =
                        drawerDetails?.body ||
                        drawerDetails?.description ||
                        drawerIssue?.body ||
                        drawerIssue?.description ||
                        drawerIssue?.content ||
                        "";
                      const parsed = parseIssueBody(rawBody);
                      const reporterEmail = formatReporterEmail(reporter) || getMetaValue(parsed.meta, "Email");
                      const reporterName = formatReporter(reporter);
                      if (canManageIssues && drawerIssueNumber) {
                        return (
                          <GiteaIssueOwnerSelect
                            issueNumber={drawerIssueNumber}
                            ownerName={reporterName}
                            ownerEmail={reporterEmail}
                            onChanged={() => {
                              // Read the issue and the list again, so the new owner shows everywhere at once.
                              setReloadNonce((n) => n + 1);
                              setDrawerReloadNonce((n) => n + 1);
                            }}
                          />
                        );
                      }
                      return (
                        <>
                          <span>{reporterName}</span>
                          {reporterEmail ? (
                            <>
                              <span>(</span>
                              <span className="font-medium text-gray-800 dark:text-gray-100">{reporterEmail}</span>
                              <span>)</span>
                            </>
                          ) : null}
                        </>
                      );
                    })()}
                  </span>
                  {canManageIssues && drawerIssueNumber && (
                    <GiteaCreateIssue key={drawerIssueNumber} sourceNumber={drawerIssueNumber} onCreated={() => {
                      setReloadNonce((n) => n + 1);
                      setDrawerReloadNonce((n) => n + 1);
                    }} />
                  )}
                  {canUseSupportViewAs && (
                    <button
                      type="button"
                      onClick={startRequesterViewAs}
                      disabled={!drawerRequester}
                      title={
                        drawerRequester
                          ? t("gitea.view_as.start_title", {
                              defaultValue: "Bekijk de applicatie read-only als deze aanvrager",
                            })
                          : t("gitea.view_as.missing_requester", {
                              defaultValue: "Geen user-id of e-mail voor de aanvrager gevonden.",
                            })
                      }
                      className="ml-1 inline-flex h-8 items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-2.5 text-xs font-semibold text-amber-900 shadow-sm transition hover:bg-amber-100 focus:outline-none focus:ring-2 focus:ring-amber-400/60 disabled:cursor-not-allowed disabled:opacity-50 dark:border-amber-700/60 dark:bg-amber-950/40 dark:text-amber-100 dark:hover:bg-amber-900/50"
                    >
                      <FaUserSecret className="h-3.5 w-3.5" />
                      <span>{t("gitea.view_as.start", { defaultValue: "View as" })}</span>
                    </button>
                  )}
                  {canNotifyApplicant && (
                    <button
                      type="button"
                      ref={notifyTriggerButtonRef}
                      onClick={() => {
                        setNotifyError(null);
                        setNotifySuccess(null);
                        setNotifyConfirmOpen(true);
                      }}
                      disabled={notifySaving || !drawerIssueNumber}
                      className="ml-1 inline-flex h-9 items-center gap-2 rounded-xl border border-indigo-200 bg-white px-3.5 text-sm font-semibold text-indigo-800 shadow-sm transition hover:bg-indigo-50 focus:outline-none focus:ring-2 focus:ring-indigo-400/60 disabled:cursor-not-allowed disabled:opacity-50 dark:border-indigo-500/40 dark:bg-gray-900 dark:text-indigo-200 dark:hover:bg-indigo-950/40"
                    >
                      <FaEnvelope className="h-3.5 w-3.5" />
                      <span>{t("gitea.notify.send_email", { defaultValue: "Send email" })}</span>
                    </button>
                  )}
                  {notifySuccess && (
                    <span className="ml-1 text-sm font-medium text-emerald-700 dark:text-emerald-300">
                      {notifySuccess}
                    </span>
                  )}
                  {notifyError && (
                    <span className="ml-2 inline-flex max-w-[360px] items-center gap-1.5 rounded-lg bg-rose-500/10 px-2 py-1 text-xs font-semibold text-rose-700 dark:bg-rose-500/15 dark:text-rose-200">
                      <FaExclamationCircle className="h-3.5 w-3.5 shrink-0" />
                      <span className="min-w-0 truncate">
                        {notifyError?.message || notifyError?.title || String(notifyError)}
                      </span>
                    </span>
                  )}
                </div>
                {(drawerDetails?.htmlUrl ?? drawerIssue?.htmlUrl) && (
                  <a
                    href={drawerDetails?.htmlUrl ?? drawerIssue?.htmlUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-2 inline-flex text-sm font-semibold text-indigo-700 hover:underline dark:text-indigo-300"
                  >
                    {t("common.open_in_new", { defaultValue: "Open in new tab" })}
                  </a>
                )}
                {config?.forgeBaseUrl && drawerIssueNumber && resolveIssueKind(drawerSource) === "development" && (
                  <a
                    href={`${String(config.forgeBaseUrl).replace(/\/+$/, "")}/projects/lumen/issues/${encodeURIComponent(drawerIssueNumber)}`}
                    target="_blank"
                    rel="noreferrer"
                    className="ml-3 mt-2 inline-flex text-sm font-semibold text-orange-700 hover:underline dark:text-orange-300"
                  >
                    {t("gitea.open_in_forge", { defaultValue: "Open in Forge" })}
                  </a>
                )}
                {drawerDetailsError && (
                  <p className="mt-2 text-sm text-red-600 dark:text-red-400">
                    {drawerDetailsError?.message ||
                      drawerDetailsError?.title ||
                      String(drawerDetailsError)}
                  </p>
                )}
                  </>
                )}
              </div>
            </div>


            {resolveIssueKind(drawerSource) === "development" && (() => {
              const source = drawerSource;
              const repository = source?.repository?.fullName ?? source?.repository?.name ?? source?.repositoryName ?? source?.repo ?? null;
              const priority = source?.priority ?? source?.priorityName ?? null;
              const labels = collectIssueLabels(source);
              const unavailable = t("common.not_available", { defaultValue: "Not available" });
              return (
                <section className="mt-0 border-t border-slate-300 px-4 py-5 dark:border-slate-700">
                  <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                    {t("gitea.metadata.title", { defaultValue: "Issue metadata" })}
                  </h4>
                  <dl className="mt-3 grid grid-cols-[7rem,1fr] gap-x-3 gap-y-2 text-sm">
                    <dt className="font-semibold">{t("gitea.filters.repository", { defaultValue: "Repository" })}</dt>
                    <dd>{repository ? <Badge tone="neutral">{repository}</Badge> : unavailable}</dd>
                    <dt className="font-semibold">{t("common.status", { defaultValue: "Status" })}</dt>
                    <dd>{source?.state ? <Badge tone={String(source.state).toLowerCase() === "open" ? "success" : "neutral"}>{source.state}</Badge> : unavailable}</dd>
                    <dt className="font-semibold">{t("gitea.filters.priority", { defaultValue: "Priority" })}</dt>
                    <dd>{priority ? <Badge tone={/high|critical|hoog|kritiek/i.test(priority) ? "danger" : /medium|middel/i.test(priority) ? "warn" : "success"}>{priority}</Badge> : unavailable}</dd>
                    <dt className="font-semibold">{t("gitea.filters.label", { defaultValue: "Labels" })}</dt>
                    <dd className="flex flex-wrap gap-1">
                      {labels.length ? labels.map((label) => (
                        <Badge
                          key={label}
                          tone={badgeToneForLabel(source, label)}
                        >
                          {label}
                        </Badge>
                      )) : unavailable}
                    </dd>
                  </dl>
                </section>
              );
            })()}



            <div
              id="gitea-detail-panel-description"
              className="mt-0 border-t border-slate-300 px-4 py-5 dark:border-slate-700"
            >
              {(() => {
                const rawBody =
                  drawerDetails?.body ||
                  drawerDetails?.description ||
                  drawerIssue?.body ||
                  drawerIssue?.description ||
                  drawerIssue?.content ||
                  "";
                const parsed = parseIssueBody(rawBody);
                const bodyImages = extractImageUrls(rawBody);
                const images = drawerImageAssets.length > 0 ? drawerImageAssets : bodyImages.map((u) => ({ resolvedUrl: u, name: "" }));
                const page = getMetaValue(parsed.meta, "Page");
                const userAgent = getMetaValue(parsed.meta, "User-Agent");
                const roles = getMetaValue(parsed.meta, "Roles");
                const when = getMetaValue(parsed.meta, "When");
                const advancedKeys = new Set(["email", "page", "user-agent", "roles", "when"]);
                const remainingMeta = parsed.meta.filter((row) => {
                  const k = String(row?.key || "").trim().toLowerCase();
                  return k && !advancedKeys.has(k);
                });

                return (
                  <>
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                        {t("common.description", { defaultValue: "Omschrijving" })}
                      </div>
                      {page ? (
                        <a
                          href={page}
                          target="_blank"
                          rel="noreferrer"
                          className="text-xs font-semibold text-indigo-700 hover:underline dark:text-indigo-300"
                        >
                          Page: {page}
                        </a>
                      ) : null}
                    </div>

                    <div className="mt-3 rounded-md border border-slate-300 bg-transparent p-4 text-sm text-gray-900 dark:border-slate-700 dark:text-gray-100">
                      {drawerDetailsLoading ? (
                        <div className="text-gray-600 dark:text-gray-300">
                          {t("common.loading", { defaultValue: "Laden…" })}
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {parsed.summary ? (
                            <LdsMarkdown>{lumenSplitLinks(parsed.summary)}</LdsMarkdown>
                          ) : (
                            <div className="text-gray-500 dark:text-gray-400">—</div>
                          )}

                          {parsed.details ? (
                            <LdsMarkdown className="text-xs text-gray-700 dark:text-gray-200">
                              {lumenSplitLinks(parsed.details)}
                            </LdsMarkdown>
                          ) : null}
                        </div>
                      )}
                    </div>

                    {(drawerAssetsLoading || drawerAssetsError || images.length > 0 || drawerOtherAssets.length > 0) && (
                      <div className="mt-4">
                        <details className="rounded-md border border-slate-300 bg-transparent p-4 text-xs text-gray-900 dark:border-slate-700 dark:text-gray-100">
                          <summary className="cursor-pointer select-none font-semibold text-gray-700 dark:text-gray-200">
                            <span className="inline-flex items-center gap-2">
                              <FaPaperclip />
                              {t("common.attachments", { defaultValue: "Bijlagen" })}
                              <span className="text-gray-500 dark:text-gray-400">
                                ({(drawerImageAssets?.length || 0) + (drawerOtherAssets?.length || 0)})
                              </span>
                            </span>
                          </summary>

                          {drawerAssetsLoading ? (
                            <div className="mt-3 text-sm text-gray-600 dark:text-gray-300">
                              {t("common.loading", { defaultValue: "Laden…" })}
                            </div>
                          ) : drawerAssetsError ? (
                            <div className="mt-3 text-sm text-red-600 dark:text-red-400">
                              {drawerAssetsError?.message ||
                                drawerAssetsError?.title ||
                                String(drawerAssetsError)}
                            </div>
                          ) : null}

                          {images.length > 0 && (
                            <div className="mt-3">
                              <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                {t("common.images", { defaultValue: "Afbeeldingen" })}
                              </div>
                              <div className="mt-2 grid grid-cols-3 gap-2 sm:grid-cols-4">
                                {images.map((img, index) => {
                                  const src = typeof img === "string" ? img : img?.resolvedUrl;
                                  const name = typeof img === "string" ? "" : img?.name || "";
                                  const objectUrl = drawerAssetObjectUrls?.[src] || "";
                                  const baseURL = http?.defaults?.baseURL || "";
                                  const protectedUrl = isProtectedAssetUrl(src, baseURL);
                                  const canDirect =
                                    !protectedUrl &&
                                    (String(src).startsWith("data:") || /^https?:\/\//i.test(String(src)));
                                  const directSrc = objectUrl || (canDirect ? src : "");
                                  const href = objectUrl || (canDirect ? src : "#");
                                  const linkHref = href === "#" ? undefined : href;
                                  return (
                                  <a
                                    key={`${index}-${src.slice(0, 20)}`}
                                    href={linkHref}
                                    target={linkHref ? "_blank" : undefined}
                                    rel={linkHref ? "noreferrer" : undefined}
                                    className="group block overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm transition hover:shadow-md dark:border-gray-700 dark:bg-gray-900"
                                    title={name || undefined}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (linkHref && canDirect) return;
                                      e.preventDefault();
                                      openDrawerAsset(src);
                                    }}
                                  >
                                    <img
                                      src={directSrc}
                                      alt={t("common.image", { defaultValue: "Afbeelding" })}
                                      className="h-16 w-full object-cover"
                                      loading="lazy"
                                      onError={(e) => {
                                        try {
                                          e.currentTarget.style.display = "none";
                                        } catch {
                                          // ignore
                                        }
                                      }}
                                    />
                                    <div className="hidden h-16 w-full items-center justify-center bg-slate-50 text-[11px] font-semibold text-slate-500 dark:bg-slate-900 dark:text-slate-300 group-has-[img:hidden]:flex">
                                      {t("common.image_unavailable", {
                                        defaultValue: "Niet beschikbaar",
                                      })}
                                    </div>
                                    {name ? (
                                      <div className="border-t border-gray-100 px-2 py-1 text-[10px] text-gray-600 dark:border-gray-800 dark:text-gray-300">
                                        <span className="block truncate">{name}</span>
                                      </div>
                                    ) : null}
                                  </a>
                                  );
                                })}
                              </div>
                            </div>
                          )}

                          {drawerOtherAssets.length > 0 && (
                            <div className="mt-4">
                              <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                {t("common.files", { defaultValue: "Bestanden" })}
                              </div>
                              <ul className="mt-2 space-y-2 text-sm">
                                {drawerOtherAssets.map((a, idx) => (
                                  <li
                                    key={`${idx}-${a.resolvedUrl}`}
                                    className="flex items-center justify-between gap-3"
                                  >
                                    <span className="flex min-w-0 items-center gap-2 text-gray-700 dark:text-gray-200">
                                      <FaPaperclip className="shrink-0 opacity-70" />
                                      <span className="truncate">{a.name || a.resolvedUrl}</span>
                                    </span>
                                  <button
                                    type="button"
                                    className="shrink-0 text-xs font-semibold text-indigo-700 hover:underline dark:text-indigo-300"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      e.preventDefault();
                                      openDrawerAsset(a.resolvedUrl);
                                    }}
                                  >
                                    {t("common.open", { defaultValue: "Open" })}
                                  </button>
                                </li>
                              ))}
                            </ul>
                            </div>
                          )}
                        </details>
                      </div>
                    )}

                    {(userAgent || roles || when || remainingMeta.length > 0) && (
                      <div className="mt-4">
                        <details className="rounded-md border border-slate-300 bg-transparent p-4 text-xs text-gray-900 dark:border-slate-700 dark:text-gray-100">
                          <summary className="cursor-pointer select-none font-semibold text-gray-700 dark:text-gray-200">
                            Meta-data
                          </summary>
                          <dl className="mt-3 space-y-2">
                            {when && (
                              <div className="grid grid-cols-[6rem,1fr] gap-2 py-1 border-b border-gray-100 last:border-b-0 dark:border-gray-800">
                                <dt className="font-semibold text-gray-700 dark:text-gray-200">When</dt>
                                <dd className="text-gray-700 dark:text-gray-200 break-words">{when}</dd>
                              </div>
                            )}
                            {userAgent && (
                              <div className="grid grid-cols-[6rem,1fr] gap-2 py-1 border-b border-gray-100 last:border-b-0 dark:border-gray-800">
                                <dt className="font-semibold text-gray-700 dark:text-gray-200">User-Agent</dt>
                                <dd className="text-gray-700 dark:text-gray-200 break-words">{userAgent}</dd>
                              </div>
                            )}
                            {roles && (
                              <div className="grid grid-cols-[6rem,1fr] gap-2 py-1 border-b border-gray-100 last:border-b-0 dark:border-gray-800">
                                <dt className="font-semibold text-gray-700 dark:text-gray-200">Roles</dt>
                                <dd className="text-gray-700 dark:text-gray-200 break-words">
                                  {(() => {
                                    const defaults = new Set([
                                      "offline_access",
                                      "uma_authorization",
                                      "default-roles-werfklas",
                                      "reader",
                                      "manage-account",
                                      "manage-account-links",
                                      "view-profile",
                                    ]);
                                    const all = roles
                                      .split(",")
                                      .map((r) => r.trim())
                                      .filter(Boolean);
                                    const important = all.filter((r) => !defaults.has(r));
                                    const hidden = all.filter((r) => defaults.has(r));
                                    const shown = drawerShowDefaultRoles ? all : important;

                                    return (
                                      <div className="space-y-2">
                                        <div className="whitespace-pre-wrap">
                                          {shown.length > 0 ? shown.join(", ") : "—"}
                                        </div>
                                        {hidden.length > 0 && (
                                          <button
                                            type="button"
                                            className="text-[11px] font-semibold text-indigo-700 underline hover:text-indigo-900 dark:text-indigo-200"
                                            onClick={() => setDrawerShowDefaultRoles((v) => !v)}
                                          >
                                            {drawerShowDefaultRoles
                                              ? t("common.less", { defaultValue: "Minder" })
                                              : t("common.more", { defaultValue: "Meer" })}
                                          </button>
                                        )}
                                      </div>
                                    );
                                  })()}
                                </dd>
                              </div>
                            )}
                            {remainingMeta.map((row) => (
                              <div
                                key={row.key}
                                className="grid grid-cols-[6rem,1fr] gap-2 py-1 border-b border-gray-100 last:border-b-0 dark:border-gray-800"
                              >
                                <dt className="font-semibold text-gray-700 dark:text-gray-200">{row.key}</dt>
                                <dd className="text-gray-700 dark:text-gray-200 break-words">
                                  {String(row.value || "—")}
                                </dd>
                              </div>
                            ))}
                          </dl>
                        </details>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>

            <div className="mt-0 border-t border-slate-300 px-4 py-5 dark:border-slate-700">
              <div className="flex items-center justify-between gap-3">
                <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  {t("profile.my_issues.comments", { defaultValue: "Comments" })}
                </div>
                <button
                  type="button"
                  onClick={() => setDrawerReloadNonce((n) => n + 1)}
                  disabled={drawerCommentsLoading || drawerDetailsLoading}
                  className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-xs font-semibold text-gray-700 shadow-sm hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 dark:hover:bg-gray-800 disabled:opacity-60"
                >
                  {drawerCommentsLoading
                    ? t("common.loading", { defaultValue: "Laden…" })
                    : t("common.refresh", { defaultValue: "Vernieuwen" })}
                </button>
              </div>

              {drawerCommentsError && (
                <p className="mt-2 text-sm text-red-600 dark:text-red-400">
                  {drawerCommentsError?.message ||
                    drawerCommentsError?.title ||
                    String(drawerCommentsError)}
                </p>
              )}

              {drawerCommentsLoading ? (
                <div className="mt-3 text-sm text-gray-600 dark:text-gray-300">
                  {t("common.loading", { defaultValue: "Laden…" })}
                </div>
              ) : sortedDrawerComments.length === 0 ? (
                <div className="mt-3 text-sm text-gray-600 dark:text-gray-300">
                  {t("profile.my_issues.no_comments", { defaultValue: "Geen comments gevonden." })}
                </div>
              ) : (
                <ul className="mt-3 space-y-2">
                  {sortedDrawerComments.map((c, idx) => {
                    const author =
                      c?.authorName || c?.author || c?.user || c?.username || c?.createdBy || "";
                    const { internalKind, isInternal, displayBody } = splitCommentBody(getCommentBody(c));
                    const created = parseDateTime(
                      c?.createdAt ?? c?.created_at ?? c?.created ?? c?.timestamp ?? null
                    );
                    const createdLabel =
                      created && !Number.isNaN(created.getTime())
                        ? created.toLocaleString()
                        : "";
                    const commentId = resolveCommentId(c);
                    const key = commentId || String(idx);
                    const canEditComment = canNotifyApplicant && Boolean(commentId);
                    const canDeleteComment = canNotifyApplicant && Boolean(commentId);
                    const editingThisComment = drawerEditingCommentId === commentId;
                    const deletingThisComment = drawerDeletingCommentId === commentId;
                    const showingEditForm = resolveCommentId(drawerEditCommentCandidate) === commentId;
                    const confirmingDelete = resolveCommentId(drawerDeleteCommentCandidate) === commentId;
                    return (
                      <li
                        key={key}
                        className={`rounded-xl border p-3 text-sm ${
                          isInternal
                            ? internalKind === "private"
                              ? "border-rose-200 bg-rose-50/70 dark:border-rose-500/30 dark:bg-rose-950/20"
                              : "border-amber-200 bg-amber-50/70 dark:border-amber-500/30 dark:bg-amber-950/20"
                            : "border-gray-200 bg-white/70 dark:border-gray-700 dark:bg-gray-900/40"
                        }`}
                      >
                        <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-gray-500 dark:text-gray-400">
                          <span className="inline-flex items-center gap-2">
                            <span className="font-semibold text-gray-800 dark:text-gray-200">
                              {author || t("common.unknown", { defaultValue: "Onbekend" })}
                            </span>
                            {isInternal && (
                              <span
                                className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide ${
                                  internalKind === "private"
                                    ? "bg-rose-500/15 text-rose-700 dark:text-rose-200"
                                    : "bg-amber-500/15 text-amber-800 dark:text-amber-200"
                                }`}
                              >
                                {internalKind === "private" ? "Private" : "Internal"}
                              </span>
                            )}
                          </span>
                          <span className="inline-flex items-center gap-2">
                            {createdLabel && <span>{createdLabel}</span>}
                            {canEditComment && (
                              <button
                                type="button"
                                onClick={() => startDrawerCommentEdit(c)}
                                disabled={editingThisComment || deletingThisComment}
                                className="inline-flex h-7 w-7 items-center justify-center rounded-lg text-indigo-600 transition hover:bg-indigo-50 hover:text-indigo-800 focus:outline-none focus:ring-2 focus:ring-indigo-400/60 disabled:cursor-not-allowed disabled:opacity-50 dark:text-indigo-300 dark:hover:bg-indigo-950/40 dark:hover:text-indigo-100"
                                title={t("gitea.comments.edit", { defaultValue: "Comment bewerken" })}
                                aria-label={t("gitea.comments.edit", { defaultValue: "Comment bewerken" })}
                              >
                                <FaPen className="h-3.5 w-3.5" />
                              </button>
                            )}
                            {canDeleteComment && (
                              <button
                                type="button"
                                onClick={() => {
                                  setDrawerEditCommentCandidate(null);
                                  setDrawerDeleteCommentCandidate(c);
                                }}
                                disabled={deletingThisComment || editingThisComment}
                                className="inline-flex h-7 w-7 items-center justify-center rounded-lg text-rose-600 transition hover:bg-rose-50 hover:text-rose-800 focus:outline-none focus:ring-2 focus:ring-rose-400/60 disabled:cursor-not-allowed disabled:opacity-50 dark:text-rose-300 dark:hover:bg-rose-950/40 dark:hover:text-rose-100"
                                title={t("gitea.comments.delete", { defaultValue: "Comment verwijderen" })}
                                aria-label={t("gitea.comments.delete", { defaultValue: "Comment verwijderen" })}
                              >
                                <FaTrash className="h-3.5 w-3.5" />
                              </button>
                            )}
                          </span>
                        </div>
                        {showingEditForm ? (
                          <div className="mt-3 rounded-xl border border-indigo-200 bg-indigo-50/60 p-3 shadow-sm dark:border-indigo-500/30 dark:bg-indigo-950/20">
                            <textarea
                              value={drawerEditCommentBody}
                              onChange={(e) => setDrawerEditCommentBody(e.target.value)}
                              rows={4}
                              className="w-full resize-y rounded-xl border border-indigo-200 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-400/60 dark:border-indigo-500/30 dark:bg-gray-900 dark:text-gray-100"
                              placeholder={t("profile.my_issues.add_comment_placeholder", {
                                defaultValue: "Write a comment…",
                              })}
                            />
                            <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
                              <button
                                type="button"
                                onClick={() => setDrawerEditCommentInternal((v) => !v)}
                                disabled={editingThisComment}
                                className={`inline-flex items-center gap-2 rounded-lg border px-3 py-1.5 text-xs font-semibold shadow-sm transition focus:outline-none focus:ring-2 focus:ring-amber-400/60 disabled:opacity-60 ${
                                  drawerEditCommentInternal
                                    ? "border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100 dark:border-amber-600/70 dark:bg-amber-950/40 dark:text-amber-100 dark:hover:bg-amber-900/50"
                                    : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 dark:hover:bg-gray-800"
                                }`}
                                aria-pressed={drawerEditCommentInternal}
                              >
                                {drawerEditCommentInternal ? (
                                  <FaLock className="h-3.5 w-3.5" />
                                ) : (
                                  <FaLockOpen className="h-3.5 w-3.5" />
                                )}
                                <span>
                                  {drawerEditCommentInternal
                                    ? t("gitea.comments.internal", { defaultValue: "Internal" })
                                    : t("gitea.comments.public", { defaultValue: "Publiek" })}
                                </span>
                              </button>
                              <div className="flex flex-wrap justify-end gap-2">
                                <button
                                  type="button"
                                  onClick={cancelDrawerCommentEdit}
                                  disabled={editingThisComment}
                                  className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 shadow-sm hover:bg-gray-50 disabled:opacity-60 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 dark:hover:bg-gray-800"
                                >
                                  {t("common.cancel", { defaultValue: "Annuleren" })}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => saveDrawerCommentEdit(c)}
                                  disabled={editingThisComment || !String(drawerEditCommentBody || "").trim()}
                                  className="rounded-lg border border-indigo-300 bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:opacity-60 dark:border-indigo-500/60 dark:bg-indigo-700 dark:hover:bg-indigo-600"
                                >
                                  {editingThisComment
                                    ? t("common.saving", { defaultValue: "Saving…" })
                                    : t("common.save", { defaultValue: "Opslaan" })}
                                </button>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <LdsMarkdown className="mt-2 text-gray-800 dark:text-gray-100">
                            {lumenSplitLinks(displayBody || "—")}
                          </LdsMarkdown>
                        )}
                        {confirmingDelete && !showingEditForm && (
                          <div className="mt-3 rounded-xl border border-rose-200 bg-rose-50/80 p-3 text-sm text-rose-900 shadow-sm dark:border-rose-500/30 dark:bg-rose-950/20 dark:text-rose-100">
                            <div className="font-semibold">
                              {t("gitea.comments.delete_confirm", {
                                defaultValue: "Deze comment verwijderen?",
                              })}
                            </div>
                            <div className="mt-2 flex flex-wrap justify-end gap-2">
                              <button
                                type="button"
                                onClick={() => setDrawerDeleteCommentCandidate(null)}
                                disabled={deletingThisComment}
                                className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 shadow-sm hover:bg-gray-50 disabled:opacity-60 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 dark:hover:bg-gray-800"
                              >
                                {t("common.cancel", { defaultValue: "Annuleren" })}
                              </button>
                              <button
                                type="button"
                                onClick={() => deleteDrawerComment(c)}
                                disabled={deletingThisComment}
                                className="rounded-lg border border-rose-300 bg-rose-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-rose-500 disabled:opacity-60 dark:border-rose-500/60 dark:bg-rose-700 dark:hover:bg-rose-600"
                              >
                                {deletingThisComment
                                  ? t("common.actions.deleting", { defaultValue: "Verwijderen..." })
                                  : t("common.actions.delete", { defaultValue: "Verwijderen" })}
                              </button>
                            </div>
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            <div className="-mt-2 px-4 pb-5">
              <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                {t("profile.my_issues.add_comment", { defaultValue: "Add comment" })}
              </div>
              {drawerNewCommentError && (
                <p className="mt-2 text-sm text-red-600 dark:text-red-400">
                  {drawerNewCommentError?.message ||
                    drawerNewCommentError?.title ||
                    String(drawerNewCommentError)}
                </p>
              )}
              <textarea
                value={drawerNewComment}
                onChange={(e) => setDrawerNewComment(e.target.value)}
                rows={4}
                className="mt-2 w-full resize-y rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-400/60 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
                placeholder={t("profile.my_issues.add_comment_placeholder", { defaultValue: "Write a comment…" })}
              />
              <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
                <button
                  type="button"
                  onClick={() => setDrawerNewCommentInternal((v) => !v)}
                  className={`inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-xs font-semibold shadow-sm transition focus:outline-none focus:ring-2 focus:ring-amber-400/60 ${
                    drawerNewCommentInternal
                      ? "border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100 dark:border-amber-600/70 dark:bg-amber-950/40 dark:text-amber-100 dark:hover:bg-amber-900/50"
                      : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 dark:hover:bg-gray-800"
                  }`}
                  aria-pressed={drawerNewCommentInternal}
                  title={
                    drawerNewCommentInternal
                      ? t("gitea.comments.internal_on_title", {
                          defaultValue: "Internal comment: alleen zichtbaar voor medewerkers",
                        })
                      : t("gitea.comments.internal_off_title", {
                          defaultValue: "Publieke comment: zichtbaar voor de aanvrager",
                        })
                  }
                >
                  {drawerNewCommentInternal ? <FaLock className="h-3.5 w-3.5" /> : <FaLockOpen className="h-3.5 w-3.5" />}
                  <span>
                    {drawerNewCommentInternal
                      ? t("gitea.comments.internal", { defaultValue: "Internal" })
                      : t("gitea.comments.public", { defaultValue: "Publiek" })}
                  </span>
                </button>
                <button
                  type="button"
                  onClick={submitDrawerComment}
                  disabled={
                    drawerNewCommentSaving ||
                    drawerCommentsLoading ||
                    drawerDetailsLoading ||
                    !String(drawerNewComment || "").trim() ||
                    !drawerIssueNumber
                  }
                  className="lumen-glow-button text-sm disabled:opacity-60"
                >
                  {drawerNewCommentSaving
                    ? t("common.saving", { defaultValue: "Saving…" })
                    : t("profile.my_issues.add_comment_submit", { defaultValue: "Post comment" })}
                </button>
              </div>
            </div>
          </aside>
        </div>
      )}
    </LdsOverviewPage>
  );
}
